<template>
  <div id="app" class="container-fluid">
    <full_orthopedic_head :visit_id="visit_id" />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading assessments...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>

      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-bone"></i> Orthopedic Assessments</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Add Assessment
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">
            No orthopedic assessments recorded yet for this visit.
          </div>
          <div v-else class="assessment-list">
            <div class="assessment-card" v-for="record in records" :key="record.id">
              <div class="assessment-card-header">
                <div>
                  <strong>{{ record.affected_site || "Unspecified site" }}</strong>
                  <span v-if="record.laterality" class="badge-site">{{ record.laterality }}</span>
                  <span v-if="record.onset" class="badge-onset">{{ record.onset }}</span>
                </div>
                <div class="assessment-actions">
                  <span class="assessment-date">{{ formatDateTime(record.assessed_at) }}</span>
                  <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="assessment-body">
                <div class="assessment-row">
                  <span v-if="record.pain_score !== null && record.pain_score !== ''"
                    ><strong>Pain:</strong> {{ record.pain_score }}/10</span
                  >
                  <span v-if="record.gait"><strong>Gait:</strong> {{ record.gait }}</span>
                  <span v-if="record.muscle_power"
                    ><strong>Power:</strong> {{ record.muscle_power }}</span
                  >
                  <span v-if="record.range_of_motion"
                    ><strong>ROM:</strong> {{ record.range_of_motion }}</span
                  >
                </div>
                <div class="assessment-flags">
                  <span v-if="record.swelling" class="flag">Swelling</span>
                  <span v-if="record.deformity" class="flag">Deformity</span>
                  <span v-if="record.bruising" class="flag">Bruising</span>
                  <span v-if="record.open_wound" class="flag flag-danger">Open Wound</span>
                </div>
                <p v-if="record.provisional_diagnosis" class="assessment-text">
                  <strong>Provisional Diagnosis:</strong> {{ record.provisional_diagnosis }}
                </p>
                <p v-if="record.treatment_plan" class="assessment-text">
                  <strong>Plan:</strong> {{ record.treatment_plan }}
                </p>
                <p v-if="record.notes" class="assessment-text">
                  <strong>Notes:</strong> {{ record.notes }}
                </p>
                <p v-if="record.assessed_by" class="assessment-meta">
                  Assessed by {{ record.assessed_by }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <OrthopedicAssessmentModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_orthopedic_head from "./full_orthopedic_head.vue";
import OrthopedicAssessmentModal from "./OrthopedicAssessmentModal.vue";
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "OrthopedicAssessmentView",
  components: { full_orthopedic_head, OrthopedicAssessmentModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(
          `orthopedic_assessments/visit/${this.visit_id}`,
          "GET",
          {},
          1
        );
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load assessments";
        }
      } catch (err) {
        console.error("Error fetching orthopedic assessments:", err);
        this.error = "An error occurred while loading assessments. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },

    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },

    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },

    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this assessment?")) return;
      try {
        const response = await generalApiRequest(
          `orthopedic_assessments/${id}`,
          "DELETE",
          {},
          1
        );
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete assessment");
        }
      } catch (error) {
        console.error("Error deleting orthopedic assessment:", error);
        alert("Failed to delete assessment. Please try again.");
      }
    },

    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}
.container-fluid {
  padding: 12px;
  background-color: #f5f7fa;
  min-height: 100vh;
}
.main-content {
  margin-top: 12px;
}
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 10px;
}
.loading-container i {
  font-size: 3rem;
  color: #2c7a7b;
  margin-bottom: 1rem;
}
.error-container i {
  font-size: 3rem;
  color: #dc3545;
  margin-bottom: 1rem;
}
.btn-retry {
  background: #2c7a7b;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
}
.card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}
.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}
.card-header h3 i {
  color: #2c7a7b;
}
.btn-edit-header {
  background: linear-gradient(135deg, #2c7a7b 0%, #285e61 100%);
  color: white;
  border: none;
  padding: 7px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}
.card-body {
  padding: 16px 20px;
}
.no-data {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-size: 13px;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 6px;
}
.assessment-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.assessment-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}
.assessment-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 8px;
}
.badge-site,
.badge-onset {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: #e2e8f0;
  color: #2d3748;
}
.assessment-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.assessment-date {
  font-size: 12px;
  color: #6c757d;
}
.btn-icon {
  background: none;
  border: none;
  color: #2c7a7b;
  cursor: pointer;
  padding: 4px 6px;
}
.btn-icon-danger {
  color: #dc3545;
}
.assessment-body {
  padding: 12px 14px;
}
.assessment-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #495057;
  margin-bottom: 8px;
}
.assessment-flags {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.flag {
  background: #fff3cd;
  color: #856404;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.flag-danger {
  background: #f8d7da;
  color: #721c24;
}
.assessment-text {
  font-size: 13px;
  color: #495057;
  margin: 4px 0;
}
.assessment-meta {
  font-size: 11px;
  color: #6c757d;
  font-style: italic;
  margin-top: 8px;
}
</style>
